import React from 'react';
import type { AlgorithmUpdateDetail } from '../../../../services/optimizationService';

interface AlgorithmDetailModalProps {
  detail: AlgorithmUpdateDetail | null;
  onClose: () => void;
}

export const AlgorithmDetailModal: React.FC<AlgorithmDetailModalProps> = ({ detail, onClose }) => {
  if (!detail) return null;

  const isLoss = detail.direction === 'loss';
  const isGain = detail.direction === 'gain';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-lg">
              <i className="ri-google-line"></i>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {detail.name}
                </h3>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                    detail.impact_level === 'high'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                      : detail.impact_level === 'medium'
                      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                  }`}
                >
                  {detail.impact_level} {detail.direction}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Rollout: {new Date(detail.started_at).toLocaleDateString()} ~{' '}
                {detail.ended_at ? new Date(detail.ended_at).toLocaleDateString() : 'Rolling Out'}{' '}
                ({detail.status === 'rolling_out' ? 'In Progress' : 'Completed'})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Baseline vs Post Rollout Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
              <span className="text-xs text-gray-400 font-medium block mb-1">Pre-Rollout Baseline</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {detail.baseline_daily_clicks != null ? `${detail.baseline_daily_clicks.toFixed(1)} clicks/day` : '—'}
              </span>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
              <span className="text-xs text-gray-400 font-medium block mb-1">Post-Rollout Average</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {detail.post_daily_clicks != null ? `${detail.post_daily_clicks.toFixed(1)} clicks/day` : '—'}
              </span>
            </div>

            <div className={`p-4 rounded-xl border text-center ${
              isLoss
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : isGain
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700'
            }`}>
              <span className="text-xs text-gray-400 font-medium block mb-1">Traffic Impact Delta</span>
              <span className={`text-lg font-extrabold ${isLoss ? 'text-red-600' : isGain ? 'text-emerald-600' : 'text-gray-700 dark:text-gray-300'}`}>
                {detail.delta_pct != null ? `${detail.delta_pct > 0 ? '+' : ''}${detail.delta_pct}%` : '0%'}
              </span>
            </div>
          </div>

          {/* About / Description */}
          {detail.about && (
            <div className="p-4 rounded-xl bg-blue-50/60 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
              <span className="font-bold block mb-1">About this Algorithm Update:</span>
              {detail.about}
            </div>
          )}

          {/* Top Affected Pages */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center justify-between">
              <span>Top Affected URLs</span>
              <span className="text-xs text-gray-400 font-normal">
                {detail.pages_affected != null && detail.pages_total != null
                  ? `${detail.pages_affected} of ${detail.pages_total} pages affected`
                  : `${detail.top_pages?.length || 0} pages affected`}
              </span>
            </h4>

            {detail.top_pages && detail.top_pages.length > 0 ? (
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 uppercase font-semibold border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-2.5">Page URL</th>
                      <th className="px-4 py-2.5 text-right">Traffic Delta</th>
                      <th className="px-4 py-2.5 text-right">Click Diff</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 font-mono">
                    {detail.top_pages.map((p: any, idx) => {
                      const url = p.url || p.path || '/';
                      const deltaPct = p.delta_pct ?? (p.loss_pct != null ? -Math.abs(p.loss_pct) : 0);
                      const clickDiff = p.delta_clicks ?? (p.clicks_lost != null ? -Math.abs(p.clicks_lost) : 0);

                      return (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-4 py-2.5 font-sans font-medium text-gray-900 dark:text-white truncate max-w-sm">
                            {url}
                          </td>
                          <td className="px-4 py-2.5 text-right font-bold">
                            <span className={deltaPct < 0 ? 'text-red-600' : 'text-emerald-600'}>
                              {deltaPct > 0 ? `+${deltaPct}` : deltaPct}%
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">
                            {clickDiff > 0 ? `+${clickDiff}` : `${clickDiff}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">No specific URL drops recorded for this update.</p>
            )}
          </div>

          {/* Adjustments & Recommendations */}
          {detail.adjustments && detail.adjustments.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Recommended Adjustments & Mitigations
              </h4>
              <div className="space-y-2.5">
                {detail.adjustments.map((adj: any, idx) => {
                  const tag = adj.tag || 'UPDATE';
                  const text = adj.text || adj.name || adj.description || 'Review content depth and quality signals.';
                  return (
                    <div
                      key={idx}
                      className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 flex items-start space-x-3 text-xs"
                    >
                      <span className="px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-bold uppercase text-[10px] flex-shrink-0 mt-0.5">
                        #{tag}
                      </span>
                      <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                        {text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Guidance Note */}
          {detail.guidance && (
            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-900 dark:text-amber-200 leading-relaxed flex items-start space-x-2.5">
              <i className="ri-lightbulb-line text-base text-amber-600 flex-shrink-0 mt-0.5"></i>
              <span>{detail.guidance}</span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900/40">
          <a
            href={detail.status_dashboard_url || 'https://status.search.google.com/'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
          >
            <span>Google Search Status Dashboard</span>
            <i className="ri-external-link-line"></i>
          </a>

          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
